import { createFileRoute } from '@tanstack/react-router';
import { Grid } from '@mantine/core';
import { Crosshair, Lock, Mail, Target } from 'lucide-react';
import { motion } from 'framer-motion';
import { useDeferredValue, useMemo, useState } from 'react';
import { CatalogFilters, ErrorState, PageContainer, PageHeader, Section } from '../components/ui';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';
import { staggerContainer, cardItem } from '../lib/motion';
import { ScenarioCard } from '../components/scenarios/ScenarioCard';
import { useScenarios } from '../hooks/useScenarios';
import { useEntitlements } from '../hooks/useAdmin';
import { selectRole, useAuthStore } from '../store/useAuthStore';
import { Button, Card } from '../components/ui';
import { Stack, Text, Title, Group } from '@mantine/core';
import type { ScenarioDomain } from '../api/types';

/**
 * Scenario catalog — list view.
 *
 * Filters: free-text search + chip-based domain filter. Search is deferred
 * (`useDeferredValue`) so typing doesn't block the grid render; the chip
 * filter and grid are memoized to avoid full re-renders on each keystroke.
 */

const DOMAIN_LABELS: ReadonlyArray<{ key: ScenarioDomain; label: string }> = [
  { key: 'threat-hunting', label: 'Threat Hunting' },
  { key: 'incident-response', label: 'Incident Response' },
  { key: 'forensics', label: 'Forensics' },
  { key: 'malware-analysis', label: 'Malware' },
  { key: 'ot-ics', label: 'OT/ICS' },
  { key: 'soc-ops', label: 'SOC Ops' },
  { key: 'network-defense', label: 'Network Defense' },
  { key: 'cloud-security', label: 'Cloud Security' },
  { key: 'purple-team', label: 'Purple Team' },
];

export const Route = createFileRoute('/scenarios/')({
  component: ScenariosIndex,
});

function ScenariosIndex() {
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [activeDomains, setActiveDomains] = useState<ReadonlySet<ScenarioDomain>>(
    () => new Set(),
  );

  const role = useAuthStore(selectRole);
  const { scenariosAllowed, licenseValid, loading: entLoading } = useEntitlements();
  const gated = role !== 'superadmin' && !entLoading && !scenariosAllowed;

  const { data, isPending, isError, error, refetch } = useScenarios(deferredSearch);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (activeDomains.size === 0) return data;
    return data.filter((s) => activeDomains.has(s.domain));
  }, [data, activeDomains]);

  const toggleDomain = (key: ScenarioDomain) => {
    setActiveDomains((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // All hooks above run unconditionally (React rules of hooks). Branch only here.
  // While entitlements load, hold the catalog back so a gated org never flashes
  // the scenario list before the lock screen.
  if (role !== 'superadmin' && entLoading) {
    return (
      <PageContainer size="wide">
        <PageHeader icon={Target} title="Scenario Catalog"
          description="Full-scope attack ranges with detection wired end-to-end."
          breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Scenarios' }]} />
        <Skeleton height={280} radius="md" />
      </PageContainer>
    );
  }
  if (gated) return <ScenariosLocked licenseValid={licenseValid} />;

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={Target}
        title="Scenario Catalog"
        description="Browse ready-to-deploy attack scenarios. Each card spins up an isolated range with detection wired end-to-end."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Scenarios' }]}
      />

      {/* Filters are a TOOLBAR, not a section.
          They had a section heading and a sentence of description ("Filters" /
          "Narrow the catalog by free-text search or one or more defense
          domains") explaining a search box and some chips. That is ~150px of
          the viewport spent on chrome: on a 950px screen the first scenario
          card began below y=500, so half the page was furniture before any
          content. A catalog's job is to get the reader to the catalog.
          Shared with the labs catalog via CatalogFilters so the two can't drift. */}
      <div className="mb-5">
        <CatalogFilters
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search scenarios, techniques, domains…"
          searchLabel="Filter scenarios"
          chips={DOMAIN_LABELS}
          active={activeDomains}
          onToggle={toggleDomain}
        />
      </div>

      <Section
        title="Scenarios"
        // The count is what a filtered catalog most needs to say, and it says
        // it in the space the removed heading used to occupy.
        action={
          filtered.length > 0 ? (
            <span className="font-mono text-[12px] text-txt-2">
              {filtered.length}
              {filtered.length !== (data?.length ?? filtered.length)
                ? ` of ${data?.length}`
                : ''}
            </span>
          ) : null
        }
      >
        {isError ? (
          <ErrorState
            title="Could not load scenarios"
            error={error}
            onRetry={() => void refetch()}
          />
        ) : isPending ? (
          <Grid gutter="md">
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid.Col key={i} span={{ base: 12, sm: 6, lg: 4 }}>
                <Skeleton height={172} radius="md" />
              </Grid.Col>
            ))}
          </Grid>
        ) : filtered.length === 0 ? (
          <EmptyState
            illustration={<Crosshair size={48} />}
            title="No scenarios match"
            description="Try clearing the active domain filters or adjusting your search."
            cta={{
              label: 'Clear filters',
              onClick: () => {
                setSearch('');
                setActiveDomains(new Set());
              },
              variant: 'secondary',
            }}
          />
        ) : (
          <motion.div variants={staggerContainer} initial="hidden" animate="show">
            <Grid gutter="md">
              {filtered.map((scenario) => (
                <Grid.Col key={scenario.slug} span={{ base: 12, sm: 6, lg: 4 }}>
                  <motion.div variants={cardItem}>
                    <ScenarioCard scenario={scenario} />
                  </motion.div>
                </Grid.Col>
              ))}
            </Grid>
          </motion.div>
        )}
      </Section>
    </PageContainer>
  );
}

/** Fancy commercial-gate shown when the org lacks the scenarios entitlement or a
 * valid license. The real enforcement is server-side (deploy returns 403); this
 * is the polished front-door. */
function ScenariosLocked({ licenseValid }: { licenseValid: boolean }) {
  const expired = !licenseValid;
  return (
    <PageContainer size="default">
      <PageHeader
        icon={Target}
        title="Scenario Catalog"
        description="Full-scope attack ranges with detection wired end-to-end."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Scenarios' }]}
      />
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 24 }}>
        <Card style={{ maxWidth: 560, width: '100%', textAlign: 'center',
          background: 'linear-gradient(160deg, rgba(57,217,138,0.06), rgba(75,123,236,0.06))',
          borderColor: 'var(--c-bd)' }}>
          <Stack gap="lg" align="center" style={{ padding: '20px 8px' }}>
            <div style={{ width: 72, height: 72, borderRadius: 20, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              background: 'rgba(57,217,138,0.10)', border: '1px solid rgba(57,217,138,0.35)' }}>
              <Lock size={32} color="rgb(var(--c-accent, 57 217 138))" strokeWidth={1.75} />
            </div>
            <Stack gap={6} align="center">
              <Title order={2} style={{ fontSize: 22 }} c="rgb(var(--c-txt-bright))">
                {expired ? 'Your license has lapsed' : 'Scenarios is a commercial add-on'}
              </Title>
              <Text size="sm" c="dimmed" style={{ maxWidth: 440 }}>
                {expired
                  ? 'Your organization’s license is no longer valid, so live scenario ranges are disabled. Renew your license to restore access.'
                  : 'Live, full-scope attack ranges with end-to-end detection are available on a commercial plan. Ask us to enable scenarios for your organization and we’ll provision the quota.'}
              </Text>
            </Stack>
            <Group gap="sm">
              <a href="mailto:support@cyberelites.ae?subject=Enable%20Scenarios%20for%20my%20organization"
                 style={{ textDecoration: 'none' }}>
                <Button variant="primary" leftIcon={<Mail size={14} />}>Contact support@cyberelites.ae</Button>
              </a>
            </Group>
            <Text size="xs" c="dimmed">Reference: commercial scenarios entitlement · quota request</Text>
          </Stack>
        </Card>
      </div>
    </PageContainer>
  );
}
